import { Injectable, OnDestroy } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  IRetryPolicy,
  LogLevel,
  RetryContext,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, share, switchMap } from 'rxjs/operators';
import { ISignalRNotification } from '../interfaces/signalr-notification';
import { LogService } from './log.service';
import { TokenService } from './token.service';

/** Must match OrganizationChangeNotifier.MessageSource on the backend. */
export const ORGANIZATION_CHANGED_SOURCE = 'OrganizationChanged';

/**
 * Backoff between reconnect attempts. The last entry is reused for every attempt after it, so the
 * schedule never runs out.
 *
 * This replaces `withAutomaticReconnect()`'s default, which is `[0, 2000, 10000, 30000, null]` -
 * the trailing null is a terminator, not a delay. That gives up roughly 42 seconds after the
 * connection drops and never tries again, so any outage longer than that leaves the tab with a dead
 * hub until the page is reloaded. Nothing here treats a dead hub as a reason to fail safe: the tab
 * stays logged in and silently stops hearing forced logouts and organization changes.
 */
export const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000, 30000];

/**
 * Up to 25% is added to each delay.
 *
 * Every tab of every user reconnects off the same server-side event, so without jitter they would
 * all retry on the same schedule and arrive together - hardest at exactly the moment the server is
 * least able to absorb it.
 */
export const RECONNECT_JITTER_RATIO = 0.25;

/** The delay before attempt number `previousAttempts`, counting from zero. */
export function reconnectDelayMs(previousAttempts: number): number {
  const index = Math.min(Math.max(previousAttempts, 0), RECONNECT_DELAYS_MS.length - 1);
  const base = RECONNECT_DELAYS_MS[index];

  return Math.round(base * (1 + RECONNECT_JITTER_RATIO * Math.random()));
}

/**
 * Retries forever, rather than giving up after four attempts the way the SignalR default does -
 * for as long as `canRetry` allows.
 *
 * `nextRetryDelayInMilliseconds` returning a number means "retry after this long"; returning null
 * is what stops reconnection for good. It returns null only once `canRetry` says no: a tab with no
 * token can only be refused, and before this a signed-out tab re-negotiated every few seconds for as
 * long as the page stayed open - 123,852 of the 144,988 negotiates prod served on 2026-11-02 were
 * those 401s.
 */
export class PersistentRetryPolicy implements IRetryPolicy {
  constructor(private canRetry: () => boolean = () => true) {}

  public nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
    if (!this.canRetry()) return null;
    return reconnectDelayMs(retryContext.previousRetryCount);
  }
}

/**
 * Shared SignalR service that handles connection setup, reconnection, and message handling
 * for all SignalR connections in the application.
 */
@Injectable({
  providedIn: 'root',
})
export class SignalRService implements OnDestroy {
  private hubConnection: HubConnection;
  private connectionEstablished = new BehaviorSubject<boolean>(false);

  /**
   * Pending restart, if one is scheduled. Holding it serves two purposes: it stops a second restart
   * being queued on top of the first, and it can be cancelled on teardown so a destroyed service
   * does not reopen a connection nobody is listening to.
   */
  private restartTimer: ReturnType<typeof setTimeout> | null = null;

  /** How many consecutive restart attempts have failed, for the backoff. Reset on success. */
  private restartAttempts = 0;

  /** Set by ngOnDestroy so a deliberate teardown is not mistaken for a dropped connection. */
  private stopped = false;

  /**
   * Set by stop() when the session ends, cleared by the initializeConnection() of the next sign-in.
   * Unlike `stopped` this is not final: the service is root-provided and outlives the session, so the
   * same connection is reused by whoever signs in next in this tab.
   */
  private idle = false;

  /** The stop() in progress, so a sign-in straight after it does not start a connection still closing. */
  private stopping: Promise<void> = Promise.resolve();
  private messageSubject = new Subject<ISignalRNotification>();
  // A plain Subject, not a BehaviorSubject: these are point-in-time change notifications, and
  // replaying the last batch to a newly opened grid made it re-fetch rows nothing had changed.
  private dataChangedSubject = new Subject<number[]>();

  // Public observables
  public connectionEstablished$ = this.connectionEstablished.asObservable();
  public messages$ = this.messageSubject.asObservable().pipe(share());
  public dataChanged$ = this.dataChangedSubject.asObservable();

  constructor(private logger: LogService, private tokenService: TokenService) {}

  /**
   * Initialize the SignalR connection
   * @returns Observable<boolean> that emits true when the connection is established
   */
  public initializeConnection(): Observable<boolean> {
    if (!this.hubConnection) {
      this.logger.log('Initializing SignalR connection...');

      this.hubConnection = new HubConnectionBuilder()
        .withUrl('api/dashboardhub', {
          // The JS client sends this as an Authorization header for negotiate/long-polling, and
          // falls back to an `access_token` query string param for WebSockets - which is what
          // TokenHandler's SignalR fallback on the backend reads.
          accessTokenFactory: () => this.tokenService.getAccessToken() ?? '',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        .withAutomaticReconnect(new PersistentRetryPolicy(() => this.hasToken()))
        .configureLogging(LogLevel.Error)
        .build();

      // Set up message handlers
      this.setupMessageHandlers();

      // Start the connection
      this.startConnection();
    } else if (this.idle) {
      // The session that stopped this connection is over and a new one is asking for it. The
      // handlers registered at build time are still attached, so subscribers hear the new session.
      this.idle = false;
      this.stopping.then(() => this.startConnection());
    }

    return this.connectionEstablished$;
  }

  /**
   * Closes the connection because the session ended, and keeps it closed until the next sign-in calls
   * initializeConnection() again.
   *
   * Every way a session ends reaches this through AuthService.processLogout. Without it nothing
   * closed the connection at all: the service is root-provided, so ngOnDestroy only runs when the
   * page unloads, and a signed-out tab kept retrying with no token for as long as it stayed open.
   * Closing it also means the tab that signed out never hears its own UserLogout broadcast.
   */
  public stop(): void {
    this.idle = true;

    if (this.restartTimer !== null) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }

    if (this.hubConnection) {
      // Also cancels an automatic reconnect in progress. The onclose this raises finds `idle` set and
      // queues nothing.
      this.stopping = this.hubConnection
        .stop()
        .then(() => this.logger.log('SignalR connection stopped for sign-out'))
        .catch(err => this.logger.error('Error stopping SignalR connection:', err));
    }
  }

  /** Whether an attempt to connect could succeed at all - with no token the server can only refuse. */
  private hasToken(): boolean {
    return !!this.tokenService.getAccessToken();
  }

  /**
   * Get messages filtered by source
   * @param source The source to filter by
   * @returns Observable of filtered messages
   */
  public getMessagesBySource(source: string): Observable<ISignalRNotification> {
    return this.messages$.pipe(
      filter(
        notification =>
          notification &&
          notification.source &&
          notification.source.toLowerCase().includes(source.toLowerCase())
      )
    );
  }

  /**
   * Get logout notifications
   * @returns Observable of logout notifications
   */
  public getLogoutNotifications(): Observable<ISignalRNotification> {
    return this.initializeConnection().pipe(
      switchMap(() => this.getMessagesBySource('UserLogout'))
    );
  }

  /**
   * Get notifications that the selected organization was changed from another session.
   *
   * The organization is one row shared by every tab, window and device this user has open, while
   * the id that scopes their queries is a claim inside each tab's own token - so a switch made
   * anywhere else leaves this tab querying the organization it was opened in. The server excludes
   * the connection that asked for the change, so this only ever fires for the other sessions.
   *
   * @returns Observable of organization change notifications
   */
  public getOrganizationChangeNotifications(): Observable<ISignalRNotification> {
    return this.initializeConnection().pipe(
      switchMap(() => this.getMessagesBySource(ORGANIZATION_CHANGED_SOURCE))
    );
  }

  /**
   * This tab's connection id, or null before the connection is up. SignalRConnectionInterceptor
   * puts it on every request so the server can leave this tab out of a push it caused.
   */
  public getConnectionId(): string | null {
    return this.hubConnection?.connectionId ?? null;
  }

  /**
   * Get tank data change notifications
   * @returns Observable of TankIds whose cache rows were just updated
   */
  public getTankChanges(): Observable<number[]> {
    return this.dataChanged$;
  }

  /**
   * Set up message handlers for the hub connection
   */
  private setupMessageHandlers(): void {
    // Handle general notifications
    this.hubConnection.on('ReceiveMessage', (notification: ISignalRNotification) => {
      this.logger.log('Received SignalR notification:', notification);
      this.messageSubject.next(notification);
    });

    // Handle tank data changes
    this.hubConnection.on('DataChanged', (changedTankIds: number[]) => {
      this.logger.log('Signal DataChanged event detected', changedTankIds);
      this.dataChangedSubject.next(changedTankIds);
    });

    // Log connection state changes
    this.hubConnection.onreconnecting(error => {
      this.logger.log('SignalR reconnecting:', error);
      this.connectionEstablished.next(false);
    });

    this.hubConnection.onreconnected(connectionId => {
      this.logger.log('SignalR reconnected. ConnectionId:', connectionId);
      this.restartAttempts = 0;
      this.connectionEstablished.next(true);
    });

    // Reached when automatic reconnect is not in play at all - the first start() never succeeded,
    // or reconnection was abandoned. PersistentRetryPolicy means a dropped connection should not
    // end up here, but "should not" is not a reason to leave the tab with no way back: before this,
    // onclose logged and did nothing, so the hub stayed dead until the page was reloaded.
    this.hubConnection.onclose(error => {
      this.logger.error('SignalR connection closed:', error);
      this.connectionEstablished.next(false);
      this.scheduleRestart(error);
    });
  }

  /**
   * Start the SignalR connection
   */
  private startConnection(): void {
    this.logger.log('Starting SignalR connection...');

    this.hubConnection
      .start()
      .then(() => {
        this.logger.log(
          'SignalR Hub Connection started. ConnectionId:',
          this.hubConnection.connectionId
        );
        this.restartAttempts = 0;
        this.connectionEstablished.next(true);
      })
      .catch(err => {
        this.logger.error('Error starting SignalR connection:', err);
        this.connectionEstablished.next(false);

        // Automatic reconnect only covers a connection that was established at least once, so a
        // tab opened while the server or Redis is down has no retry of its own. Without this it
        // would never connect for the life of the page.
        this.scheduleRestart(err);
      });
  }

  /**
   * Queue another start attempt, backing off as they keep failing.
   */
  private scheduleRestart(error: unknown): void {
    if (this.stopped || this.idle || this.restartTimer !== null) {
      return;
    }

    // A token cleared without going through stop() - a refresh that failed - leaves nothing a retry
    // could succeed with. Marked idle, so the next sign-in's initializeConnection() starts it again.
    if (!this.hasToken()) {
      this.idle = true;
      return;
    }

    const delay = reconnectDelayMs(this.restartAttempts);
    this.restartAttempts += 1;

    this.logger.log(`Retrying the SignalR connection in ${delay}ms.`, error);

    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;

      if (!this.stopped && !this.idle) {
        this.startConnection();
      }
    }, delay);
  }

  /**
   * Clean up resources when the service is destroyed
   */
  ngOnDestroy(): void {
    // Before stopping, so the close handler stop() triggers does not queue a restart.
    this.stopped = true;

    if (this.restartTimer !== null) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }

    if (this.hubConnection) {
      this.hubConnection
        .stop()
        .then(() => this.logger.log('SignalR connection stopped'))
        .catch(err => this.logger.error('Error stopping SignalR connection:', err));
    }
  }
}
